import { BackendSource, backendSources } from "@/shared/api/firehose";
import { useRef } from "react";

import { createStore, useStore } from "zustand";

type Filters = {
  sourcesFilter?: string[];

  portfoliosFilter?: number[];

  portfoliosHighlight?: number[];

  sourcesHighlight?: string[];

  selectedAdvancedFilters?: number[]; // not implemented yet
};

type FilterStoreData = {
  [K in Partial<BackendSource>]: Filters;
};

type FilterStoreActions = {
  allStories: Filters;

  toggleSourceFilter: (
    source: BackendSource | "allStories",
    filter: string
  ) => void;

  togglePortfolioFilter: (
    source: BackendSource | "allStories",
    filter: number
  ) => void;

  togglePortfolioHighlight: (
    source: BackendSource | "allStories",
    filter: number
  ) => void;

  toggleSourceHighlight: (
    source: BackendSource | "allStories",
    filter: string
  ) => void;

  setPortfoliosToFilter: (
    source: BackendSource | "allStories",
    filters: number[]
  ) => void;

  setPortfoliosToHighlight: (
    source: BackendSource | "allStories",
    filters: number[]
  ) => void;

  setSourcesToHighlight: (
    source: BackendSource | "allStories",
    filters: string[]
  ) => void;

  setSourcesToFilter: (
    source: BackendSource | "allStories",
    filters: string[]
  ) => void;

  toggleAdvancedFilter: (
    source: BackendSource | "allStories",
    filter: number
  ) => void;

  setSelectedAdvancedFilters: (
    source: BackendSource | "allStories",
    filters: number[]
  ) => void;
};

export type FirehoseFilterStore = FilterStoreData & FilterStoreActions;

const initialState = {
  ...backendSources.reduce((acc: FilterStoreData, source) => {
    acc[source] = {};

    return acc;
  }, {} as FilterStoreData),

  allStories: {},
};

export const createFirehoseFilterStore = () =>
  createStore<FirehoseFilterStore>((set) => ({
    ...initialState,

    toggleSourceFilter: (source, filter) => {
      set((state) => {
        const sourceFilter = state[source].sourcesFilter || [];

        const newSourceFilter = sourceFilter.includes(filter)
          ? sourceFilter.filter((f) => f !== filter)
          : [...sourceFilter, filter];

        return {
          [source]: {
            ...state[source],
            sourcesFilter: newSourceFilter,
          },
        };
      });
    },

    togglePortfolioFilter: (source, filter) => {
      set((state) => {
        const portfolioFilter = state[source].portfoliosFilter || [];

        const newFilter = portfolioFilter.includes(filter)
          ? portfolioFilter.filter((f) => f !== filter)
          : [...portfolioFilter, filter];

        return {
          [source]: {
            ...state[source],
            portfoliosFilter: newFilter,
          },
        };
      });
    },

    togglePortfolioHighlight: (source, filter) => {
      set((state) => {
        const highlightFilter = state[source].portfoliosHighlight || [];

        const newFilter = highlightFilter.includes(filter)
          ? highlightFilter.filter((f) => f !== filter)
          : [...highlightFilter, filter];

        return {
          [source]: {
            ...state[source],
            portfoliosHighlight: newFilter,
          },
        };
      });
    },

    toggleSourceHighlight(source, filter) {
      set((state) => {
        const highlightFilter = state[source].sourcesHighlight || [];

        const newFilters = highlightFilter.includes(filter)
          ? highlightFilter.filter((f) => f !== filter)
          : [...highlightFilter, filter];

        return {
          [source]: {
            ...state[source],
            sourcesHighlight: newFilters,
          },
        };
      });
    },

    setSourcesToHighlight(
      source: BackendSource | "allStories",
      filters: string[]
    ) {
      set((state) => ({
        [source]: {
          ...state[source],
          sourcesHighlight: filters,
        },
      }));
    },

    setSourcesToFilter(
      source: BackendSource | "allStories",
      filters: string[]
    ) {
      set((state) => ({
        [source]: {
          ...state[source],
          sourcesFilter: filters,
        },
      }));
    },

    setPortfoliosToHighlight(
      source: BackendSource | "allStories",
      filters: number[]
    ) {
      set((state) => ({
        [source]: {
          ...state[source],
          portfoliosHighlight: filters,
        },
      }));
    },

    setPortfoliosToFilter(
      source: BackendSource | "allStories",
      filters: number[]
    ) {
      set((state) => ({
        [source]: {
          ...state[source],
          portfoliosFilter: filters,
        },
      }));
    },

    toggleAdvancedFilter: (source, filter) => {
      set((state) => {
        const advancedFilters = state[source].selectedAdvancedFilters || [];
        const newFilters = advancedFilters.includes(filter)
          ? advancedFilters.filter((f) => f !== filter)
          : [...advancedFilters, filter];
        return {
          [source]: {
            ...state[source],
            selectedAdvancedFilters: newFilters,
          },
        };
      });
    },

    setSelectedAdvancedFilters: (source, filters) => {
      set((state) => ({
        [source]: {
          ...state[source],
          selectedAdvancedFilters: filters,
        },
      }));
    },
  }));

const GlobalFilterStore = createFirehoseFilterStore();

export const useFirehoseFilterStore = <T>(
  selector: (state: FirehoseFilterStore) => T
) => useStore(GlobalFilterStore, selector);

export const useLocalFirehoseFilterStore = () => {
  const storeRef = useRef<ReturnType<typeof createFirehoseFilterStore>>();
  if (!storeRef.current) storeRef.current = createFirehoseFilterStore();
  return storeRef.current;
};
